import { useRef, useSyncExternalStore } from 'react';

/**
 * A ~1KB external store used instead of React Context for high-frequency state
 * (presence deltas, notification counters, ringing calls).
 *
 * WHY: a Context provider re-renders its whole subtree on every value change.
 * Presence alone can flap dozens of times a second across a roster; that would
 * repaint the entire shell. Here, only components that select a *changed* slice
 * re-render, and the store itself costs one Set of callbacks.
 *
 * Selectors MUST be referentially stable (module-level functions or
 * `useCallback`) and should return primitives or memoised references.
 */
export interface TinyStore<T> {
  get: () => T;
  set: (updater: T | ((prev: T) => T)) => void;
  subscribe: (listener: () => void) => () => void;
}

export function createStore<T>(initial: T): TinyStore<T> {
  let state = initial;
  const listeners = new Set<() => void>();

  return {
    get: () => state,
    set: (updater) => {
      const next =
        typeof updater === 'function' ? (updater as (prev: T) => T)(state) : updater;
      if (Object.is(next, state)) return;
      state = next;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Shallow equality for selectors that must return a fresh object/array. */
export function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) =>
    Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
}

/**
 * Subscribe to a slice of a store. The snapshot is cached behind `isEqual` so
 * `useSyncExternalStore` never loops on a freshly-allocated selector result.
 */
export function useStoreValue<T, S>(
  store: TinyStore<T>,
  selector: (state: T) => S,
  isEqual: (a: S, b: S) => boolean = Object.is,
): S {
  const cache = useRef<{ value: S; primed: boolean }>({
    value: undefined as unknown as S,
    primed: false,
  });

  const getSnapshot = (): S => {
    const next = selector(store.get());
    if (!cache.current.primed || !isEqual(cache.current.value, next)) {
      cache.current = { value: next, primed: true };
    }
    return cache.current.value;
  };

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}
